import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import {
  useApi,
  ApiError,
  type CaseloadResponse,
  type CaseloadKaimahi,
  type CaseloadUnresolved,
  type CaseloadProvisionResponse,
  type WhanauListResponse,
  type Whanau,
} from '../api/client';
import './ManagerCaseload.css';

/** Pull a useful message out of an ApiError body, or fall back. Logs full error to console. */
function extractApiError(err: unknown, fallback: string): string {
   
  console.error('[caseload]', err);
  if (err instanceof ApiError) {
    const body = err.body as { error?: unknown } | null;
    if (body && typeof body.error === 'string') return body.error;
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

/**
 * Manager caseload assignment editor at /manager/caseload.
 *
 * Drill-down UX matching the framework editor:
 *   1. Page shows a kaimahi-card grid (org-wide across WR + Family Start).
 *      Each card surfaces assignment count + key/co-worker breakdown.
 *   2. An "Unresolved Exess workers" section shows assignments whose UPN
 *      hasn't matched any signed-in kaimahi yet — manageable signal for
 *      missing UPN mappings.
 *   3. Click a kaimahi tile → modal opens with all their current
 *      whānau assignments, with Remove + Add controls.
 *
 * Writes hit /api/v1/manager/caseload directly per action — no save bar.
 * Manager actions are individually small and the UI refreshes optimistically.
 *
 * Permission: team.reassign (manager+ default). Server-enforced.
 */

export function ManagerCaseload() {
  const { t } = useTranslation();
  const callApi = useApi();

  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'ok'; data: CaseloadResponse }
    | { kind: 'error'; message: string }
  >({ kind: 'loading' });

  const [selectedKaimahiOid, setSelectedKaimahiOid] = useState<string | null>(null);

  async function load() {
    try {
      const data = await callApi<CaseloadResponse>('/manager/caseload');
      setState({ kind: 'ok', data });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? typeof (err.body as { error?: unknown })?.error === 'string'
            ? (err.body as { error: string }).error
            : err.message
          : err instanceof Error
            ? err.message
            : 'Failed to load caseload';
      setState({ kind: 'error', message });
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state.kind === 'loading') {
    return (
      <section className="mc">
        <h1 className="mc__title">{t('caseload.title')}</h1>
        <p className="mc__status">{t('caseload.loading')}</p>
      </section>
    );
  }

  if (state.kind === 'error') {
    return (
      <section className="mc">
        <h1 className="mc__title">{t('caseload.title')}</h1>
        <p className="mc__error">{state.message}</p>
      </section>
    );
  }

  const selected =
    selectedKaimahiOid !== null
      ? state.data.kaimahi.find((k) => k.kaimahi_oid === selectedKaimahiOid) ?? null
      : null;

  return (
    <section className="mc">
      <header className="mc__header">
        <h1 className="mc__title">{t('caseload.title')}</h1>
        <p className="mc__subtitle">{t('caseload.subtitle')}</p>
        <div className="mc__counts">
          <span>
            {t('caseload.countsResolvedKaimahi', { count: state.data.counts.kaimahi_resolved })}
          </span>
          <span>
            {t('caseload.countsResolvedAssignments', {
              count: state.data.counts.assignments_resolved,
            })}
          </span>
          {state.data.counts.workers_unresolved > 0 && (
            <span className="mc__counts--warn">
              {t('caseload.countsUnresolved', {
                count: state.data.counts.workers_unresolved,
              })}
            </span>
          )}
        </div>
      </header>

      <div className="mc__grid">
        {state.data.kaimahi.map((k) => (
          <KaimahiTile
            key={k.kaimahi_oid}
            kaimahi={k}
            onClick={() => setSelectedKaimahiOid(k.kaimahi_oid)}
          />
        ))}
      </div>

      {state.data.unresolved.length > 0 && (
        <UnresolvedSection
          unresolved={state.data.unresolved}
          onRefresh={load}
        />
      )}

      {selected && (
        <KaimahiModal
          kaimahi={selected}
          allKaimahi={state.data.kaimahi}
          onClose={() => setSelectedKaimahiOid(null)}
          onRefresh={load}
        />
      )}
    </section>
  );
}

export default ManagerCaseload;

// ============================================================================
// Kaimahi tile (page grid)
// ============================================================================

function KaimahiTile({
  kaimahi,
  onClick,
}: {
  kaimahi: CaseloadKaimahi;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const keyCount = kaimahi.assignments.filter((a) => a.is_key_worker).length;
  const coCount = kaimahi.assignments.length - keyCount;

  return (
    <button type="button" className="mc-tile" onClick={onClick}>
      <span className="mc-tile__name">
        {kaimahi.display_name ?? kaimahi.upn}
      </span>
      <span className="mc-tile__upn">{kaimahi.upn}</span>
      <span className="mc-tile__counts">
        <span className="mc-chip mc-chip--key">
          {t('caseload.tile.keyCount', { count: keyCount })}
        </span>
        {coCount > 0 && (
          <span className="mc-chip">
            {t('caseload.tile.coCount', { count: coCount })}
          </span>
        )}
      </span>
    </button>
  );
}

// ============================================================================
// Unresolved Exess workers section
// ============================================================================

function UnresolvedSection({
  unresolved,
  onRefresh,
}: {
  unresolved: CaseloadUnresolved[];
  onRefresh: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const callApi = useApi();
  const [busy, setBusy] = useState<string | null>(null);

  async function provision(upns: string[], busyKey: string) {
    setBusy(busyKey);
    try {
      const body = upns.length === 0
        ? { all: true }
        : { worker_upns: upns };
      const result = await callApi<CaseloadProvisionResponse>(
        '/manager/caseload/provision',
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      const s = result.summary;
      // Show a short summary if anything failed to resolve.
      if (s.not_found > 0 || s.errors > 0) {
        window.alert(
          t('caseload.provisionSummary', {
            provisioned: s.provisioned,
            notFound: s.not_found,
            errors: s.errors,
          }),
        );
      }
      await onRefresh();
    } catch (err) {
      window.alert(extractApiError(err, 'Failed to provision'));
    } finally {
      setBusy(null);
    }
  }

  const provisionableUpns = unresolved
    .map((u) => u.worker_upn)
    .filter((u): u is string => typeof u === 'string' && u.includes('@'));

  return (
    <section className="mc__unresolved">
      <header className="mc__unresolved-head">
        <div>
          <h2 className="mc__section-title">
            {t('caseload.unresolvedHeading')}{' '}
            <span className="mc__count">({unresolved.length})</span>
          </h2>
          <p className="mc__unresolved-hint">{t('caseload.unresolvedHint')}</p>
        </div>
        {provisionableUpns.length > 0 && (
          <button
            type="button"
            className="mc-button mc-button--primary"
            disabled={busy === 'all'}
            onClick={() => void provision([], 'all')}
          >
            {busy === 'all'
              ? t('caseload.provisionAllBusy')
              : t('caseload.provisionAll', { count: provisionableUpns.length })}
          </button>
        )}
      </header>

      <ul className="mc__unresolved-list">
        {unresolved.map((u) => {
          const fullName =
            u.worker_first_names || u.worker_last_name
              ? `${u.worker_first_names ?? ''} ${u.worker_last_name ?? ''}`.trim()
              : t('caseload.unknownName');
          const canProvision =
            typeof u.worker_upn === 'string' && u.worker_upn.includes('@');
          const busyKey = `one-${u.exess_worker_id}`;
          return (
            <li key={u.exess_worker_id} className="mc-unresolved-row">
              <div className="mc-unresolved-row__head">
                <strong>{fullName}</strong>
                {u.worker_upn && (
                  <span className="mc-unresolved-row__upn">{u.worker_upn}</span>
                )}
              </div>
              <div className="mc-unresolved-row__actions">
                <span className="mc-unresolved-row__count">
                  {t('caseload.unresolved.assignmentCount', {
                    count: u.assignments.length,
                  })}
                </span>
                {canProvision && u.worker_upn && (
                  <button
                    type="button"
                    className="mc-button"
                    disabled={busy === busyKey}
                    onClick={() => void provision([u.worker_upn as string], busyKey)}
                  >
                    {busy === busyKey
                      ? t('caseload.provisionBusy')
                      : t('caseload.provisionOne')}
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ============================================================================
// Kaimahi detail modal
// ============================================================================

function KaimahiModal({
  kaimahi,
  allKaimahi: _allKaimahi,
  onClose,
  onRefresh,
}: {
  kaimahi: CaseloadKaimahi;
  allKaimahi: CaseloadKaimahi[];
  onClose: () => void;
  onRefresh: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const callApi = useApi();
  const [view, setView] = useState<{ kind: 'list' } | { kind: 'add' }>({ kind: 'list' });
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (view.kind !== 'list') setView({ kind: 'list' });
        else onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [view.kind, onClose]);

  async function handleRemove(whanau_id: number, exess_service_id: number, source: 'exess' | 'override-add') {
    const confirmMsg =
      source === 'override-add'
        ? t('caseload.confirmRemoveOverride')
        : t('caseload.confirmRemove');
    if (!window.confirm(confirmMsg)) return;
    setBusy(`remove-${whanau_id}-${exess_service_id}`);
    try {
      if (source === 'override-add') {
        // Removing an add-override = clear the override row entirely.
        await callApi('/manager/caseload', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            action: 'clear-override',
            whanau_id,
            kaimahi_oid: kaimahi.kaimahi_oid,
          }),
        });
      } else {
        // Removing an Exess-sourced assignment = write a remove override.
        await callApi('/manager/caseload', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            action: 'remove',
            whanau_id,
            kaimahi_oid: kaimahi.kaimahi_oid,
            exess_service_id,
          }),
        });
      }
      await onRefresh();
    } catch (err) {
      window.alert(extractApiError(err, 'Failed to remove assignment'));
    } finally {
      setBusy(null);
    }
  }

  async function handleAdd(whanau_id: number) {
    setBusy(`add-${whanau_id}`);
    try {
      await callApi('/manager/caseload', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          action: 'add',
          whanau_id,
          kaimahi_oid: kaimahi.kaimahi_oid,
          exess_service_id: null,
        }),
      });
      await onRefresh();
      setView({ kind: 'list' });
    } catch (err) {
      window.alert(extractApiError(err, 'Failed to add assignment'));
    } finally {
      setBusy(null);
    }
  }

  // Whānau the kaimahi is already on (across all services)
  const existingWhanauIds = useMemo(
    () => new Set(kaimahi.assignments.map((a) => a.whanau_id)),
    [kaimahi.assignments],
  );

  return createPortal(
    <div
      className="mc-modal__backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget && view.kind === 'list') onClose();
      }}
    >
      <div className="mc-modal" role="dialog" aria-modal="true">
        <header className="mc-modal__header">
          {view.kind !== 'list' && (
            <button
              type="button"
              className="mc-modal__back"
              onClick={() => setView({ kind: 'list' })}
              aria-label={t('caseload.back')}
            >
              ←
            </button>
          )}
          <h2 className="mc-modal__title">
            {view.kind === 'list'
              ? (kaimahi.display_name ?? kaimahi.upn)
              : t('caseload.addAssignmentTitle')}
          </h2>
          <button
            type="button"
            className="mc-modal__close"
            onClick={onClose}
            aria-label={t('caseload.close')}
          >
            ×
          </button>
        </header>

        <div className="mc-modal__body">
          {view.kind === 'list' ? (
            <section>
              <header className="mc-modal__section-head">
                <p className="mc-modal__upn">{kaimahi.upn}</p>
                <button
                  type="button"
                  className="mc-button mc-button--ghost"
                  onClick={() => setView({ kind: 'add' })}
                >
                  + {t('caseload.addWhanau')}
                </button>
              </header>

              <ul className="mc-modal__assignments">
                {kaimahi.assignments.length === 0 && (
                  <li className="mc-modal__empty">{t('caseload.noAssignments')}</li>
                )}
                {kaimahi.assignments.map((a) => {
                  const busyKey = `remove-${a.whanau_id}-${a.exess_service_id}`;
                  return (
                    <li
                      key={`${a.whanau_id}-${a.exess_service_id}-${a.source}`}
                      className="mc-assignment"
                    >
                      <div className="mc-assignment__main">
                        <strong className="mc-assignment__name">
                          {a.whanau_display_name ?? `#${a.whanau_id}`}
                        </strong>
                        <span className="mc-chip">{a.service_label}</span>
                        {a.is_key_worker && (
                          <span className="mc-chip mc-chip--key">
                            {t('caseload.keyWorker')}
                          </span>
                        )}
                        {a.source === 'override-add' && (
                          <span className="mc-chip mc-chip--manager">
                            {t('caseload.managerAdded')}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        className="mc-button mc-button--danger"
                        disabled={busy === busyKey}
                        onClick={() => void handleRemove(a.whanau_id, a.exess_service_id, a.source)}
                      >
                        {busy === busyKey ? t('caseload.removing') : t('caseload.remove')}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : (
            <AddAssignmentPanel
              existingWhanauIds={existingWhanauIds}
              busyKey={busy}
              onAdd={handleAdd}
              onCancel={() => setView({ kind: 'list' })}
            />
          )}
        </div>

        {view.kind === 'list' && (
          <footer className="mc-modal__footer">
            <button
              type="button"
              className="mc-button mc-button--primary"
              onClick={onClose}
            >
              {t('caseload.done')}
            </button>
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}

// ============================================================================
// Add-assignment panel — searches the whānau directory
// ============================================================================

function AddAssignmentPanel({
  existingWhanauIds,
  busyKey,
  onAdd,
  onCancel,
}: {
  existingWhanauIds: ReadonlySet<number>;
  busyKey: string | null;
  onAdd: (whanau_id: number) => Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const callApi = useApi();
  const [search, setSearch] = useState('');
  const [whanau, setWhanau] = useState<Whanau[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await callApi<WhanauListResponse>('/whanau');
        if (cancelled) return;
        setWhanau(data.rows);
      } catch {
        if (!cancelled) setWhanau([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return whanau
      .filter((w) => !existingWhanauIds.has(w.whanau_id))
      .filter((w) => {
        if (term === '') return true;
        return (w.display_name ?? '').toLowerCase().includes(term);
      })
      .slice(0, 50);
  }, [whanau, existingWhanauIds, search]);

  return (
    <section>
      <input
        type="text"
        className="mc-search"
        placeholder={t('caseload.searchPlaceholder')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        autoFocus
      />

      {loading ? (
        <p className="mc-modal__status">{t('caseload.loading')}</p>
      ) : filtered.length === 0 ? (
        <p className="mc-modal__empty">{t('caseload.noWhanauFound')}</p>
      ) : (
        <ul className="mc-modal__assignments">
          {filtered.map((w) => {
            const busy = busyKey === `add-${w.whanau_id}`;
            return (
              <li key={w.whanau_id} className="mc-assignment">
                <div className="mc-assignment__main">
                  <strong className="mc-assignment__name">
                    {w.display_name ?? `#${w.whanau_id}`}
                  </strong>
                  {w.primary_location && (
                    <span className="mc-chip">{w.primary_location}</span>
                  )}
                </div>
                <button
                  type="button"
                  className="mc-button mc-button--primary"
                  disabled={busy}
                  onClick={() => void onAdd(w.whanau_id)}
                >
                  {busy ? t('caseload.adding') : t('caseload.add')}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mc-modal__panel-actions">
        <button
          type="button"
          className="mc-button"
          onClick={onCancel}
        >
          {t('caseload.cancel')}
        </button>
      </div>
    </section>
  );
}
